/**
 * Currency utilities — single source of truth for how each currency is
 * formatted across the app (decimals, symbol, input validation, display).
 *
 * IMPORTANT: keep CURRENCY_DECIMALS in sync with the backend currency config
 * in EasyCheckAPI/EasyCheckAPI/Controllers/PrintChequesPdfController.vb
 * (the `CurrencyConfigs` dictionary). 3-decimal currencies (OMR/BHD/KWD)
 * use 1000 minor units per major unit (e.g. 1 Omani Rial = 1000 baisa).
 */

// Decimal places per currency. Unknown currencies default to 2.
export const CURRENCY_DECIMALS = {
  AED: 2,
  SAR: 2,
  QAR: 2,
  EGP: 2,
  EUR: 2,
  OMR: 3,
  BHD: 3,
  KWD: 3,
};

// Display symbols. Falls back to the currency code itself when unknown.
const CURRENCY_SYMBOLS = {
  AED: 'د.إ',
  OMR: 'OMR',
  SAR: 'SAR',
  BHD: 'BHD',
  KWD: 'KWD',
  QAR: 'QAR',
  EGP: 'EGP',
  EUR: '€',
  USD: '$',
};

/** Number of decimal places a currency uses (2 by default). */
export const getCurrencyDecimals = (code) => CURRENCY_DECIMALS[code] ?? 2;

/** Display symbol for a currency (falls back to the code). */
export const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[code] || code || '';

/** Native <input type="number"> step for the currency's precision. */
export const getAmountStep = (code) =>
  getCurrencyDecimals(code) === 3 ? 0.001 : 0.01;

/**
 * Regex that allows digits with at most the currency's decimal places.
 * Used by number inputs / grid editors to reject over-precise keystrokes.
 */
export const getAmountInputRegex = (code) =>
  new RegExp(`^\\d*\\.?\\d{0,${getCurrencyDecimals(code)}}$`);

/**
 * react-hook-form `validate` rule: ensures the entered value does not exceed
 * the currency's allowed decimal places. Returns `true` or an error string.
 */
export const validateAmountPrecision = (value, code) => {
  if (value === '' || value === null || value === undefined) return true;
  const decimals = (String(value).split('.')[1] || '').length;
  const max = getCurrencyDecimals(code);
  return decimals <= max || `Maximum ${max} decimal places for ${code}`;
};

/**
 * Format a cheque amount for display with the correct number of decimals.
 * @param {number|string} amount
 * @param {string} code currency code (e.g. 'OMR')
 * @param {{ withSymbol?: boolean }} [opts] include the currency symbol (default true)
 */
export const formatCurrencyAmount = (amount, code, { withSymbol = true } = {}) => {
  const decimals = getCurrencyDecimals(code);
  const formatted = Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return withSymbol ? `${getCurrencySymbol(code)} ${formatted}` : formatted;
};
